/**
 * Raw URL search state for the course-search page. Kept as plain strings
 * (comma-separated for multi-value fields) so the address bar stays clean and
 * shareable — `normalizeSearchParams` from @admitly/shared turns this into the
 * typed query used for the API call.
 */
export type RawSearch = {
  q?: string;
  category?: string;
  aqf?: string;
  state?: string;
  intake?: string;
  ieltsMax?: string;
  feeMin?: string;
  feeMax?: string;
  pr?: string;
  anzsco?: string;
};

export type PatchFn = (patch: Partial<RawSearch>) => void;

const KEYS: readonly (keyof RawSearch)[] = [
  'q',
  'category',
  'aqf',
  'state',
  'intake',
  'ieltsMax',
  'feeMin',
  'feeMax',
  'pr',
  'anzsco',
];

/** Keep only known keys with non-empty string values (used by validateSearch). */
export function pickRawSearch(raw: Record<string, unknown>): RawSearch {
  const out: RawSearch = {};
  for (const k of KEYS) {
    const v = raw[k];
    if (typeof v === 'string' && v.trim().length) out[k] = v;
    else if (typeof v === 'number') out[k] = String(v);
  }
  return out;
}

/** Toggle a value within a comma-separated list, returning the new list (or undefined if empty). */
export function toggleInList(current: string | undefined, value: string): string | undefined {
  const items = (current ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const idx = items.indexOf(value);
  if (idx >= 0) items.splice(idx, 1);
  else items.push(value);
  return items.length ? items.join(',') : undefined;
}

export function listIncludes(current: string | undefined, value: string): boolean {
  return (current ?? '')
    .split(',')
    .map((s) => s.trim())
    .includes(value);
}
